// Shared FAQ content: consumed by client-side accordion components AND by
// server page components (for FAQPage JSON-LD). Kept in a plain data module
// (no "use client") because Next.js treats every export of a client file as
// an opaque client reference when imported into a Server Component.

export interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

export const homeFaqs: FAQItem[] = [
  {
    id: 1,
    question: "What technology stack do you specialize in?",
    answer: "We focus on high-performance web ecosystems (Next.js, Node.js, React, and serverless architectures), cross-platform and native mobile applications (React Native and Swift/SwiftUI), robust cloud infrastructure (AWS, Azure, Kubernetes, Terraform, Docker), and custom AI pipelines (LLM integration, RAG vector pipelines, and ML automation)."
  },
  {
    id: 2,
    question: "Do you integrate with existing in-house engineering teams?",
    answer: "Absolutely. We routinely operate as principal systems architects or senior developers within existing corporate structures, helping in-house teams accelerate pipeline velocities, execute systems performance audits, resolve performance blocks, or configure specialized AI/ML features."
  },
  {
    id: 3,
    question: "How do you guarantee software quality and delivery timelines?",
    answer: "We implement strict type-safety boundaries with TypeScript/Swift/Rust, run automated end-to-end testing suites on CI/CD pipelines, and work on agile weekly sprint delivery structures. You receive a dedicated staging link to audit live build increments at any stage."
  },
  {
    id: 4,
    question: "What is your security auditing and cleansing protocol?",
    answer: "We follow a Zero-Trust cybersecurity strategy. We isolate compromised server files, run automated security rootkit scanners, hard-restrict entry port credentials, sanitize databases, secure secret variables, and deploy code within restricted private subnets."
  },
  {
    id: 5,
    question: "Do you provide post-launch support and maintenance?",
    answer: "Yes. We offer custom post-launch maintenance SLA packages. These include 24/7 server monitoring, preventive third-party updates, weekly automated backups, memory-leak sweeps, and horizontal scaling support to ensure 99.9% uptime."
  },
  {
    id: 6,
    question: "Who owns the intellectual property (IP) and source code?",
    answer: "You retain 100% full ownership of all source code, IP, repositories, and cloud accounts upon delivery. We never hold code hostage, never introduce proprietary vendor lock-in, and never charge recurring royalty fees for your software."
  },
  {
    id: 7,
    question: "Can we sign a Non-Disclosure Agreement (NDA) before sharing our project?",
    answer: "Yes. We routinely execute mutual NDAs prior to any technical discovery discussion to ensure your proprietary ideas, data workflows, and product architectures remain completely confidential."
  },
  {
    id: 8,
    question: "Do you build MVPs for early-stage startups as well as enterprise systems?",
    answer: "Yes. We operate agile sprint structures specifically designed for seed-stage and growth startups needing high-speed MVP launches, as well as complex enterprise cloud migrations and microservice architectures."
  }
];

export const contactFaqs: FAQItem[] = [
  {
    id: 1,
    question: "How quickly can we start after our initial scoping consultation?",
    answer: "Following our initial consultation, we deliver a formal technical scope proposal and sprint timeline within 48 to 72 hours. Once approved, development sprints can commence within 5 business days."
  },
  {
    id: 2,
    question: "Do you sign non-disclosure agreements (NDAs) before scoping discussions?",
    answer: "Yes. We treat intellectual property and business confidentiality with strict care. We can execute a standard mutual NDA prior to reviewing your codebases or discovery documents."
  },
  {
    id: 3,
    question: "What pricing models do you offer for software development?",
    answer: "We offer fixed-milestone scope pricing for defined product launches, as well as dedicated monthly engineering team retainers for dynamic, fast-evolving enterprise platforms."
  },
  {
    id: 4,
    question: "What happens during a 30-minute free scoping consultation?",
    answer: "You talk directly with a senior software architect. We analyze your functional requirements, data workflows, target tech stack, and timeline constraints to provide realistic recommendations and budget expectations."
  },
  {
    id: 5,
    question: "Do you work with businesses outside of New Zealand?",
    answer: "Yes. While our headquarters are in Auckland, NZ, we actively engineer software for enterprise platforms and growth-stage companies across Australia (Sydney, Melbourne, Brisbane) and global markets."
  }
];

export interface BlogFAQItem {
  q: string;
  a: string;
}

export const blogFaqs: BlogFAQItem[] = [
  {
    q: "How do you define project scoping?",
    a: "We utilize a rigorous 3-week Discovery Phase where we map architectural requirements, identify potential data bottlenecks, and define the specific LLM/AI model parameters needed for your performance goals."
  },
  {
    q: "What are typical project timelines?",
    a: "MVP development for AI-integrated web applications typically spans 12-16 weeks. Enterprise-scale infrastructure overhauls are staged over 6-9 months depending on the complexity of existing legacy systems."
  },
  {
    q: "Do you offer retainer-based support?",
    a: "Yes. Most of our enterprise partners transition into a \"Beyond Retainer\" model following deployment, which covers model fine-tuning, security patches, and performance optimization as your user base scales."
  }
];
